import type { UseFormRegisterReturn } from 'react-hook-form';
import type { SupplyProject } from '@obraplan/core/application/ports/requisition';
import { Input } from '@/components/ui/input';
import { useContainer, useSession } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';

/**
 * Para onde esta obra manda requisição de material.
 *
 * **Era um campo de texto pedindo um uuid.** O valor guardado é `obras_projetos.id` do
 * sistema de compras, e o domínio preferiu o id ao nome por um bom motivo — nome casa até
 * o dia em que alguém renomeia. Só que ninguém sabe um uuid de cor: na prática o campo
 * ficava em branco, e obra sem ele é obra que não requisita, em silêncio.
 *
 * A lista resolve os dois lados: a pessoa escolhe por nome, o sistema guarda o id.
 *
 * ## Os três estados, e por que nenhum deles some
 *
 * - **Com lista** — o normal quando a integração está configurada.
 * - **Sem integração** — a lista volta vazia, e o campo **vira texto** de novo. Não é
 *   defeito: instalação sem sistema de compras é caso previsto, e esconder o campo
 *   impediria de preencher à mão quem soubesse o valor.
 * - **Carregando** — o seletor aparece desabilitado em vez de piscar de um formato para o
 *   outro, que faria a pessoa clicar no que ainda vai mudar.
 */
export function SupplyProjectField({
  registration,
  id,
  onSelect,
}: {
  readonly registration: UseFormRegisterReturn;
  readonly id: string;
  /**
   * O destino escolhido, para quem quiser aproveitá-lo.
   *
   * O cadastro usa isto para sugerir código e nome: a lista já traz os dois, e obrigar a
   * pessoa a redigitar o que o sistema acabou de mostrar é trabalho que o computador
   * devia fazer. Ausente na edição, onde a obra já tem nome próprio e mudá-lo por causa
   * de uma troca de destino seria surpresa, não conveniência.
   */
  readonly onSelect?: (destino: SupplyProject | undefined) => void;
}) {
  const container = useContainer();
  const session = useSession();

  const resultado = useLive(() => container.listSupplyProjects.execute(session), [session.userId]);

  // Carregando: nem lista nem texto ainda. O seletor desabilitado ocupa o mesmo lugar que
  // o definitivo, então a caixa não pula quando a resposta chega.
  if (resultado === undefined) {
    return (
      <select id={id} className={CLASSE_SELECT} disabled>
        <option>Carregando destinos…</option>
      </select>
    );
  }

  // A consulta falhou, ou não há integração. Nos dois casos o campo de texto é a saída
  // honesta: quem souber o valor consegue preencher, e quem não souber deixa em branco —
  // que é o que já acontecia antes desta tela existir.
  const destinos: readonly SupplyProject[] = resultado.ok ? resultado.value : [];
  if (destinos.length === 0) {
    return <Input id={id} {...registration} />;
  }

  return (
    <select
      id={id}
      className={CLASSE_SELECT}
      {...registration}
      onChange={(evento) => {
        // A ordem importa: o formulário registra a mudança primeiro, e só então o aviso
        // sai. Invertido, quem ouve o aviso leria o valor anterior.
        void registration.onChange(evento);
        onSelect?.(destinos.find((destino) => destino.id === evento.target.value));
      }}
    >
      {/*
        A obra que não requisita é escolha legítima, e precisa de uma opção explícita:
        sem ela, o primeiro da lista viraria o padrão silencioso e obras passariam a
        requisitar material sem ninguém ter decidido isso.
      */}
      <option value="">— Não requisita material —</option>
      {destinos.map((destino) => (
        <option key={destino.id} value={destino.id} disabled={!destino.allowed}>
          {rotulo(destino)}
        </option>
      ))}
    </select>
  );
}

/**
 * O que a pessoa lê na lista.
 *
 * A sigla vem na frente porque é o que se usa no dia a dia; o empreendimento entra
 * ao lado para desempatar projetos de nome parecido, que é comum quando a mesma
 * construtora toca duas fases do mesmo terreno.
 *
 * O bloqueado continua visível, e dizendo por quê. Some da lista sem explicação vira
 * chamado de suporte — e a trava de escrita do outro lado recusaria a requisição de
 * qualquer forma, só que semanas depois, quando alguém já contava com ela.
 */
function rotulo(destino: SupplyProject): string {
  const nome = destino.code === undefined ? destino.name : `${destino.code} — ${destino.name}`;
  const dono = destino.development === undefined ? '' : ` · ${destino.development}`;
  return destino.allowed ? `${nome}${dono}` : `${nome}${dono} (ainda não liberado)`;
}

const CLASSE_SELECT =
  'h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm';
